import { useEffect, useRef, useState, type ReactNode } from 'react'

import pvStyles from './PagePreview.module.scss'

/**
 * Viewport the previewed site renders into. `width` fixes the virtual width;
 * without it the frame fills the available width, but never less than
 * `minWidth`. `height` fixes the virtual height; without it the frame keeps
 * `DEFAULT_VISIBLE_HEIGHT` on screen, whatever the scale.
 */
export interface PreviewViewport {
  width?: number
  minWidth?: number
  height?: number
}

const DEFAULT_VISIBLE_HEIGHT = 900

/**
 * Renders `children` at the viewport's virtual size and scales the result down
 * to fit the available width. The site in the iframe sees the full virtual
 * viewport, so its breakpoints match the real device. `note` shows beside the
 * size label.
 */
export function ScaledPreviewFrame({
  viewport,
  note,
  children,
}: {
  viewport: PreviewViewport
  note?: ReactNode
  children: ReactNode
}) {
  const hostRef = useRef<HTMLDivElement>(null)
  const [available, setAvailable] = useState(0)

  useEffect(() => {
    const el = hostRef.current
    if (!el) return
    setAvailable(el.clientWidth)
    if (!window.ResizeObserver) return
    const ro = new ResizeObserver(() => {
      const w = el.clientWidth
      setAvailable(cur => (Math.abs(w - cur) > 1 ? w : cur))
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const fluidWidth = Math.max(available, viewport.minWidth ?? 0)
  const virtualWidth = viewport.width ?? fluidWidth
  const scale = available > 0 ? Math.min(1, available / virtualWidth) : 1
  const virtualHeight = viewport.height ?? DEFAULT_VISIBLE_HEIGHT / scale

  return (
    <div ref={hostRef} className={pvStyles.pvScaleHost}>
      {available > 0 && (
        <>
          <div className={pvStyles.pvScaleMeta}>
            {note && <span className={pvStyles.pvScaleNote}>{note}</span>}
            <span className={pvStyles.pvScaleLabel}>
              {Math.round(virtualWidth)} × {Math.round(virtualHeight)}
              {scale < 1 && ` · ${Math.round(scale * 100)}%`}
            </span>
          </div>
          <div
            className={pvStyles.pvScaleBox}
            style={{ width: virtualWidth * scale, height: virtualHeight * scale }}
          >
            <div
              className={pvStyles.pvScaleInner}
              style={{ width: virtualWidth, height: virtualHeight, transform: `scale(${scale})` }}
            >
              {children}
            </div>
          </div>
        </>
      )}
    </div>
  )
}
